import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useSaved } from '../lib/SavedContext';
import { User, Mail, Settings, Package, LogOut, ChevronRight, Save, Check, Zap, Bookmark, ExternalLink, X, Trash2, Clock, Target, Award, Star, BarChart3, Instagram, Link2, Globe, Share2, RefreshCw } from 'lucide-react';

const INTERESTS = ['HYROX', 'CrossFit', 'Running', 'Biohacking', 'Nutrition', 'Recovery', 'Supplements', 'Strength Training', 'Peptides', 'Cold Exposure', 'Mobility'];
const FITNESS_GOALS = ['HYROX Competitor', 'General Fitness', 'Strength & Muscle', 'Endurance', 'Weight Loss', 'Biohacking & Optimization', 'Recovery & Longevity'];
const EXPERIENCE_LEVELS = [
  { id: 'beginner', label: 'Beginner', desc: 'New to structured training' },
  { id: 'intermediate', label: 'Intermediate', desc: '1-3 years of training' },
  { id: 'advanced', label: 'Advanced', desc: '3+ years, competitive' },
  { id: 'elite', label: 'Elite', desc: 'Podium-level athlete' },
];

export default function AccountPage() {
  const { user, token, logout, updateProfile, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [interests, setInterests] = useState([]);
  const [newsletter, setNewsletter] = useState(true);
  const [bio, setBio] = useState('');
  const [fitnessGoals, setFitnessGoals] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState('');
  const [socialLinks, setSocialLinks] = useState({ instagram: '', strava: '' });
  const [publicProfile, setPublicProfile] = useState(false);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/auth');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setInterests(user.preferences?.interests || []);
      setNewsletter(user.preferences?.newsletter !== false);
      setBio(user.bio || '');
      setFitnessGoals(user.fitness_goals || []);
      setExperienceLevel(user.experience_level || '');
      setSocialLinks(user.social_links || { instagram: '', strava: '' });
      setPublicProfile(user.preferences?.public_profile || false);
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/profile/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null).then(d => d && setStats(d)).catch(() => {});
    }
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        first_name: firstName,
        last_name: lastName,
        preferences: { interests, newsletter, public_profile: publicProfile },
        bio,
        fitness_goals: fitnessGoals,
        experience_level: experienceLevel,
        social_links: socialLinks,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (i) => {
    setInterests(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  };

  if (loading || !user) return null;

  return (
    <main className="min-h-screen bg-black pt-24 pb-16 px-4" data-testid="account-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-white text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>My Account</h1>
            <p className="text-zinc-500 text-sm mt-1">{user.email}</p>
          </div>
          <button onClick={() => { logout(); navigate('/'); }}
            className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-400 text-xs uppercase tracking-wider hover:border-red-500/40 hover:text-red-400 transition-colors"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-zinc-800 mb-8">
          {[
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'saved', label: 'Saved', icon: Bookmark },
            { id: 'orders', label: 'Orders', icon: Package },
            { id: 'returns', label: 'Returns', icon: RefreshCw },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-wider transition-colors ${
                activeTab === t.id ? 'text-[#e8ff00] border-b-2 border-[#e8ff00]' : 'text-zinc-500 hover:text-zinc-300'
              }`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid={`account-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6" data-testid="profile-section">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Left Column: Avatar + Stats */}
              <div className="w-full md:w-64 flex-shrink-0 space-y-4">
                {/* Avatar */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 flex items-center justify-center bg-zinc-900 border-2 border-[#e8ff00]/30 text-[#e8ff00] text-3xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="profile-avatar">
                    {(firstName?.[0] || '?').toUpperCase()}{(lastName?.[0] || '').toUpperCase()}
                  </div>
                  <p className="text-white font-heading text-lg mt-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{firstName} {lastName}</p>
                  {experienceLevel && <span className="text-xs text-zinc-500 uppercase tracking-wider">{experienceLevel} Athlete</span>}
                </div>

                {/* Stats */}
                {stats && (
                  <div className="bg-zinc-900 border border-zinc-800 p-4 space-y-3" data-testid="profile-stats">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Activity</p>
                    {[
                      { icon: Bookmark, label: 'Saved Items', value: stats.saved_items },
                      { icon: Star, label: 'Reviews Written', value: stats.reviews_written },
                      { icon: BarChart3, label: 'Comparisons', value: stats.comparisons_saved },
                      { icon: Target, label: 'Articles Read', value: stats.articles_read },
                    ].map(s => (
                      <div key={s.label} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <s.icon className="w-3.5 h-3.5 text-zinc-600" />
                          <span className="text-zinc-400 text-xs">{s.label}</span>
                        </div>
                        <span className="text-white text-sm font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Column: Edit Form */}
              <div className="flex-1 space-y-5">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>First Name</label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
                      data-testid="profile-first-name" />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Last Name</label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
                      data-testid="profile-last-name" />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Email</label>
                  <div className="flex items-center gap-2 px-4 py-3 bg-zinc-900/50 border border-zinc-800 text-zinc-400 text-sm">
                    <Mail className="w-4 h-4" /> {user.email}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Bio</label>
                  <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3}
                    placeholder="Tell us about your training journey..."
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors resize-none"
                    data-testid="profile-bio" />
                </div>

                {/* Experience Level */}
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Experience Level</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {EXPERIENCE_LEVELS.map(lvl => (
                      <button key={lvl.id} onClick={() => setExperienceLevel(lvl.id)}
                        className={`p-3 border text-left transition-colors ${
                          experienceLevel === lvl.id ? 'bg-[#e8ff00]/10 border-[#e8ff00]/40' : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                        data-testid={`exp-level-${lvl.id}`}>
                        <p className={`text-xs font-bold uppercase tracking-wider ${experienceLevel === lvl.id ? 'text-[#e8ff00]' : 'text-white'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{lvl.label}</p>
                        <p className="text-zinc-500 text-[10px] mt-0.5">{lvl.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fitness Goals */}
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Fitness Goals</label>
                  <div className="flex flex-wrap gap-2">
                    {FITNESS_GOALS.map(g => (
                      <button key={g} onClick={() => setFitnessGoals(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g])}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                          fitnessGoals.includes(g) ? 'bg-[#E63946]/10 border-[#E63946]/40 text-[#E63946]' : 'border-zinc-700 text-zinc-500 hover:text-white'
                        }`}
                        style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                        data-testid={`goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        {g}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Interests */}
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Content Interests</label>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map(i => (
                      <button key={i} onClick={() => toggleInterest(i)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                          interests.includes(i) ? 'bg-[#e8ff00]/10 border-[#e8ff00]/40 text-[#e8ff00]' : 'border-zinc-700 text-zinc-500 hover:text-white'
                        }`}
                        style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                        data-testid={`interest-${i.toLowerCase().replace(/\s+/g, '-')}`}>
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider block mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Social Links</label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Instagram className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                      <input type="text" value={socialLinks.instagram} onChange={e => setSocialLinks(prev => ({...prev, instagram: e.target.value}))}
                        placeholder="Instagram username"
                        className="flex-1 px-4 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
                        data-testid="social-instagram" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Link2 className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                      <input type="text" value={socialLinks.strava} onChange={e => setSocialLinks(prev => ({...prev, strava: e.target.value}))}
                        placeholder="Strava profile URL"
                        className="flex-1 px-4 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
                        data-testid="social-strava" />
                    </div>
                  </div>
                </div>

                {/* Newsletter */}
                <div className="flex items-center justify-between py-3 border-t border-zinc-800">
                  <div>
                    <p className="text-white text-sm">Newsletter</p>
                    <p className="text-zinc-500 text-xs">Receive training tips and equipment deals</p>
                  </div>
                  <button onClick={() => setNewsletter(!newsletter)}
                    className={`w-10 h-6 rounded-full transition-colors relative ${newsletter ? 'bg-[#e8ff00]' : 'bg-zinc-700'}`}
                    data-testid="newsletter-toggle">
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-black transition-transform ${newsletter ? 'left-5' : 'left-1'}`} />
                  </button>
                </div>

                {/* Public Profile */}
                <div className="flex items-center justify-between py-3 border-t border-zinc-800">
                  <div>
                    <p className="text-white text-sm flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-zinc-500" /> Public Profile</p>
                    <p className="text-zinc-500 text-xs">Let others view your profile, reviews, and shared plans</p>
                  </div>
                  <button onClick={() => setPublicProfile(!publicProfile)}
                    className={`w-10 h-6 rounded-full transition-colors relative ${publicProfile ? 'bg-[#e8ff00]' : 'bg-zinc-700'}`}
                    data-testid="public-profile-toggle">
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-black transition-transform ${publicProfile ? 'left-5' : 'left-1'}`} />
                  </button>
                </div>

                {/* Share Profile Link */}
                {publicProfile && user?.user_id && (
                  <div className="flex items-center gap-3 py-2">
                    <Share2 className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                    <Link to={`/profile/${user.user_id}`} className="text-[#e8ff00] text-xs hover:underline" data-testid="view-public-profile-link">
                      View your public profile
                    </Link>
                  </div>
                )}

                {/* Save Button */}
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-[#e8ff00] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid="save-profile-btn">
                  {saved ? <><Check className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Saved Tab */}
        {activeTab === 'saved' && (
          <SavedItemsTab token={token} />
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <OrderHistoryTab token={token} />
        )}

        {/* Returns Tab */}
        {activeTab === 'returns' && (
          <div className="bg-zinc-900 border border-zinc-800 p-8 text-center" data-testid="returns-tab">
            <RefreshCw className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl uppercase tracking-wider text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Returns & Exchanges
            </h3>
            <p className="text-zinc-500 text-sm mb-6 max-w-md mx-auto">
              Need to return or exchange an item? Use our returns portal powered by Redo to submit your request.
            </p>
            <a
              href="https://ninjathlete.com/a/redo/returns"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] hover:bg-[#c62d39] text-white uppercase tracking-widest text-sm font-bold transition-all duration-300"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="account-start-return-btn"
            >
              Start a Return <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-zinc-600 text-xs mt-4">
              Or visit our <Link to="/returns" className="text-zinc-400 hover:text-white underline transition-colors">returns policy</Link> for more information.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}

function OrderHistoryTab({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/orders/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(async (data) => {
        const orderList = Array.isArray(data) ? data : [];
        // Fetch timeline for each order
        const enriched = await Promise.all(orderList.map(async (o) => {
          const oid = o.order_id || o.id;
          try {
            const resp = await fetch(`${API}/api/orders/${oid}/detail`, { headers: { Authorization: `Bearer ${token}` } });
            if (resp.ok) {
              const detail = await resp.json();
              return { ...o, ...detail };
            }
          } catch { /* ignore */ }
          return o;
        }));
        setOrders(enriched);
      })
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [token, API]);

  const getTrackingUrl = (carrier, num) => {
    if (!num) return '#';
    const c = (carrier || '').toLowerCase();
    if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${num}`;
    if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${num}`;
    if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${num}`;
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${num}`;
  };

  const statusColor = {
    pending: 'text-zinc-400 bg-zinc-800 border-zinc-700',
    confirmed: 'text-[#e8ff00] bg-[#e8ff00]/10 border-[#e8ff00]/30',
    delivered: 'text-green-400 bg-green-400/10 border-green-400/30',
    shipped: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
    processing: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    cancelled: 'text-red-400 bg-red-400/10 border-red-400/30',
  };

  if (loading) return <div className="text-zinc-500 text-sm py-8">Loading orders...</div>;

  return (
    <div data-testid="orders-section">
      {orders.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900/50 border border-zinc-800">
          <Package className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 text-sm mb-2">No orders yet</p>
          <Link to="/hyrox#equipment" className="text-[#e8ff00] text-xs uppercase tracking-wider hover:underline" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Shop HYROX Equipment
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.order_id} className="bg-zinc-900/80 border border-zinc-800 p-5" data-testid={`order-${order.order_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white text-sm" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    Order #{order.order_id.slice(0, 8).toUpperCase()}
                  </p>
                  <p className="text-zinc-500 text-xs mt-1">
                    {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <span className={`px-2.5 py-1 text-[10px] uppercase tracking-wider border ${statusColor[order.status] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid={`order-status-${order.order_id}`}>
                  {order.status}
                </span>
              </div>

              {/* Items */}
              <div className="space-y-2 mb-4">
                {order.items.map((item, i) => (
                  <div key={item.name} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0">
                        <Package className="w-3.5 h-3.5 text-zinc-500" />
                      </div>
                      <div>
                        <p className="text-white text-sm">{item.name}</p>
                        <p className="text-zinc-600 text-xs">Qty: {item.qty}</p>
                      </div>
                    </div>
                    <span className="text-zinc-300 text-sm" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>${item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                <div className="flex items-center gap-4">
                  {order.discount_code && (
                    <span className="text-[#e8ff00] text-[10px] uppercase tracking-wider bg-[#e8ff00]/10 px-2 py-0.5 border border-[#e8ff00]/30" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      Code: {order.discount_code}
                    </span>
                  )}
                  {(order.tracking_number || order.carrier) && (
                    <a href={getTrackingUrl(order.carrier, order.tracking_number)} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[#06B6D4] text-xs hover:underline" data-testid={`tracking-link-${order.order_id || order.id}`}>
                      <ExternalLink className="w-3 h-3" />
                      Track: {order.tracking_number}
                    </a>
                  )}
                </div>
                <div className="text-right">
                  {order.discount > 0 && (
                    <p className="text-zinc-500 text-xs line-through">${(order.total + order.discount).toFixed(2)}</p>
                  )}
                  <p className="text-white text-lg" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>${(order.total || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Order Timeline */}
              {order.timeline && order.timeline.length > 0 && (
                <div className="mt-4 pt-3 border-t border-zinc-800/50" data-testid={`timeline-${order.order_id || order.id}`}>
                  <p className="text-zinc-600 text-[10px] uppercase tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Order Timeline</p>
                  <div className="space-y-2">
                    {order.timeline.map((evt, i) => (
                      <div key={evt.timestamp} className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          evt.status === 'delivered' ? 'bg-green-400' : evt.status === 'shipped' ? 'bg-[#06B6D4]' : evt.status === 'cancelled' ? 'bg-red-400' : 'bg-zinc-600'
                        }`} />
                        <div>
                          <span className="text-white text-xs uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{evt.status}</span>
                          {evt.note && <span className="text-zinc-500 text-xs ml-2">- {evt.note}</span>}
                          <p className="text-zinc-600 text-[10px]">{new Date(evt.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SavedItemsTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toggleSaved } = useSaved();
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchItems = () => {
    fetch(`${API}/api/saved/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchItems(); }, [token]); // eslint-disable-line

  const handleRemove = async (item) => {
    await toggleSaved({ type: item.type, item_id: item.item_id });
    setItems(prev => prev.filter(i => !(i.item_id === item.item_id && i.type === item.type)));
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter);
  const articleCount = items.filter(i => i.type === 'article').length;
  const productCount = items.filter(i => i.type === 'product').length;

  if (loading) return <div className="text-zinc-500 text-sm py-8">Loading saved items...</div>;

  return (
    <div data-testid="saved-section">
      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { v: 'all', l: `All (${items.length})` },
          { v: 'article', l: `Articles (${articleCount})` },
          { v: 'product', l: `Equipment (${productCount})` },
        ].map(f => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
              filter === f.v ? 'text-[#e8ff00] border-[#e8ff00]/40 bg-[#e8ff00]/5' : 'text-zinc-500 border-zinc-700 hover:text-white'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid={`saved-filter-${f.v}`}>
            {f.l}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900/50 border border-zinc-800">
          <Bookmark className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 text-sm mb-2">
            {items.length === 0 ? 'No saved items yet' : `No saved ${filter === 'article' ? 'articles' : 'equipment'}`}
          </p>
          <Link to="/blog" className="text-[#e8ff00] text-xs uppercase tracking-wider hover:underline" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Browse Articles
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(item => (
            <div key={`${item.type}-${item.item_id}`}
              className="bg-zinc-900/80 border border-zinc-800 hover:border-zinc-700 p-4 flex items-start gap-4 transition-colors group"
              data-testid={`saved-item-${item.item_id}`}>

              {/* Image */}
              {item.image ? (
                <div className="w-20 h-16 flex-shrink-0 overflow-hidden bg-zinc-800">
                  <img src={item.image} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-20 h-16 flex-shrink-0 bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                  {item.type === 'product' ? <Package className="w-5 h-5 text-zinc-600" /> : <Bookmark className="w-5 h-5 text-zinc-600" />}
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {item.type === 'article' ? (
                      <Link to={`/blogs/article/${item.item_id}`} className="text-white text-sm font-medium hover:text-[#e8ff00] transition-colors line-clamp-1" data-testid={`saved-link-${item.item_id}`}>
                        {item.title}
                      </Link>
                    ) : (
                      <Link to={item.slug ? `/shop/${item.slug}` : `/shop`} className="text-white text-sm font-medium hover:text-[#e8ff00] transition-colors line-clamp-1" data-testid={`saved-link-${item.item_id}`}>
                        {item.title}
                      </Link>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      {item.category && (
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{item.category}</span>
                      )}
                      {item.price && (
                        <span className="text-[#e8ff00] text-xs" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{item.price}</span>
                      )}
                      {item.saved_at && (
                        <span className="text-zinc-600 text-[10px] flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          {new Date(item.saved_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {item.excerpt && (
                      <p className="text-zinc-500 text-xs mt-1 line-clamp-1">{item.excerpt}</p>
                    )}
                  </div>

                  {/* Remove button */}
                  <button onClick={() => handleRemove(item)}
                    className="text-zinc-600 hover:text-red-400 transition-colors p-1 flex-shrink-0 opacity-0 group-hover:opacity-100"
                    data-testid={`saved-remove-${item.item_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
